import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "./BrandIcons";
import Seo from "../components/Seo";
import { ROUTE_META, softwareApplicationSchema } from "../seo/siteMeta.mjs";

const platformCards = [
  { icon: "ai", title: "AI GRC Assistant", tag: "AI Enabled", text: "Generate control summaries, evidence requests, risk narratives, audit responses, and remediation actions faster." },
  { icon: "control", title: "Control Management", tag: "Core GRC", text: "Track control ownership, status, review cadence, exceptions, and framework mappings across your compliance program." },
  { icon: "evidence", title: "Evidence Automation", tag: "Audit Ready", text: "Collect, tag, validate, and reuse audit evidence so teams stop chasing screenshots and disconnected files." },
  { icon: "map", title: "Asset + Control Mapping", tag: "Context", text: "Connect assets, threats, vulnerabilities, controls, and findings to understand what matters most to the business." },
  { icon: "risk", title: "Risk Register", tag: "Risk Intelligence", text: "Centralize risks, owners, impact, likelihood, remediation plans, business cost, and executive status in one place." },
  { icon: "dashboard", title: "Executive Dashboards", tag: "Board Ready", text: "Show risk heatmaps, compliance posture, audit progress, overdue evidence, and remediation priority to leadership." },
  { icon: "web", title: "Dark Web Data", tag: "Threat Intelligence", text: "Monitor dark web forums and channels for exposed credentials, leaked assets, and external threats to prevent breaches before they happen." },
  { icon: "scales", title: "FAIR® Analysis", tag: "Risk Quantification", text: "Quantify financial risk exposure using standard frameworks to calculate potential losses and prioritize security investments." },
];

const flowItems = [
  { title: "Evidence-Driven Controls", bar: "Continuously validate control effectiveness with real-time, audit-ready evidence.", desc: "Ensure every security and compliance control is backed by current, verifiable evidence—reducing manual effort while maintaining continuous audit readiness." },
  { title: "Business-Driven Risk Prioritization", bar: "Prioritize the risks that matter most to your business.", desc: "Automatically identify, quantify, and prioritize open risks based on business impact, likelihood, regulatory obligations, and operational resilience—so leadership focuses on what truly matters." },
  { title: "Critical Asset Intelligence", bar: "Know what matters. Protect what matters most.", desc: "Continuously identify and map your organization's most critical assets, business services, and data to risks, controls, threats, and ownership for complete enterprise visibility." },
  { title: "Independent Threat Validation", bar: "Transform external cyber threats into trusted security evidence.", desc: "Independently validate dark web intelligence and external exposures, then map verified findings directly to controls, assets, and the risk register—empowering proactive, evidence-based risk decisions before attackers can exploit vulnerabilities." },
];

const terminalRows = [
  { ts: "08:14:02", lvl: "PASS", cls: "tok", msg: "SOC 2 CC6.1 evidence validated" },
  { ts: "08:22:17", lvl: "RISK", cls: "twarn", msg: "Vendor access review overdue by 7 days" },
  { ts: "08:36:44", lvl: "MAP", cls: "tinfo", msg: "NIST CSF PR.AA mapped to ISO 27001 A.5" },
  { ts: "08:48:09", lvl: "HIGH", cls: "thigh", msg: "Critical asset missing MFA enforcement evidence" },
  { ts: "09:01:31", lvl: "DONE", cls: "tok", msg: "Audit packet generated for PCI-DSS access controls" },
  { ts: "09:16:55", lvl: "AI", cls: "tai", msg: "Drafted remediation narrative for control owner" },
];

const frameworksRow1 = ["SOC 2", "NIST 800-53", "ISO-27001", "PCI-DSS", "HIPAA", "GDPR", "FedRAMP"];
const frameworksRow2 = ["MITRE ATT&CK", "HITRUST", "DORA", "Custom Controls"];

const testimonials = [
  { quote: "DarkDefensive gave our lean GRC team a single source of truth. Evidence is ready before auditors even ask.", who: "Elena D.", role: "Head of Compliance, Fintech" },
  { quote: "We went from scrambling every audit season to continuous readiness. The AI assistant saves hours each week.", who: "Marcus R.", role: "CISO, SaaS Platform" },
  { quote: "Mapping controls once and reporting across every framework cut our duplicate work dramatically.", who: "Priya S.", role: "Risk Manager, Healthcare" },
  { quote: "The dark web intelligence feeding straight into our risk register is a genuine differentiator.", who: "Tom K.", role: "Security Lead, Retail" },
];

const Home = () => {
  const location = useLocation();

  // Scroll to a section when navigated from another page (header/footer nav)
  // or when the URL carries a hash.
  useEffect(() => {
    const targetId = location.state?.scrollTo || location.hash?.replace("#", "");
    if (targetId) {
      // Wait for layout so the target exists and has its final position.
      const t = setTimeout(() => {
        const el = document.getElementById(targetId);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
      return () => clearTimeout(t);
    }
  }, [location]);

  return (
    <div className="dd-page" data-page="home">
      <Seo
        path="/"
        title={ROUTE_META["/"].title}
        description={ROUTE_META["/"].description}
        schema={[softwareApplicationSchema]}
      />

      {/* HERO + STATS */}
      <div className="band band-circuit">
        <div className="wrap hero">
          <p className="eyebrow">AI-Powered SaaS GRC Risk Suite</p>
          <h1>
            Automate Compliance.
            <br />
            <span className="accent">Reduce Cyber Risk.</span>
          </h1>
          <p className="lede">
            Dark Defensive empowers GRC teams across your organization to manage
            controls, evidence, risks, threats, assets, and audits in one
            intelligent platform &mdash; complete with executive-ready reporting
            and continuous audit readiness.
          </p>
          <div className="dd-btn-row">
            <Link className="dd-btn dd-btn-primary" to="/contact-us">
              Request a Demo &gt;
            </Link>
            <a className="dd-btn dd-btn-secondary" href="#platform">
              Explore Platform
            </a>
          </div>

          <ul className="stats" aria-label="Platform outcomes">
            <li className="stat"><b>1x</b><span>Unified Risk View</span></li>
            <li className="stat"><b>80%</b><span>Less Evidence Chasing</span></li>
            <li className="stat"><b>24/7</b><span>Continuous Readiness</span></li>
            <li className="stat"><b>10+</b><span>Frameworks Mapped</span></li>
          </ul>
        </div>
      </div>

      {/* PLATFORM */}
      <div className="wrap section" id="platform">
        <h2>
          One platform for <span className="accent">GRC</span>,{" "}
          <span className="accent">risk</span>,{" "}
          <span className="accent">audit</span>,<br />&amp;{" "}
          <span className="accent-soft">threat visibility</span>
        </h2>
        <p className="lede">
          Replace spreadsheets, scattered evidence folders, and manual status
          meetings with a connected SaaS workflow designed for lean cybersecurity
          and compliance teams.
        </p>

        <div className="card-grid">
          {platformCards.map((c) => (
            <article className="card" key={c.title}>
              <span className="icon"><Icon name={c.icon} /></span>
              <h3>{c.title}</h3>
              <p>{c.text}</p>
              <p className="tag">{c.tag}</p>
            </article>
          ))}
        </div>
      </div>

      {/* GRC AUTOMATION */}
      <div className="wrap section" id="grc-automation">
        <h2>
          Turn fragmented compliance work into
          <br />one <span className="accent">intelligent workflow</span>
        </h2>
        <p className="lede">
          Dark Defensive helps teams move from reactive audit preparation to
          continuous governance &mdash; with clear ownership, measurable risk, and
          evidence that is ready when auditors ask.
        </p>

        <div className="workflow-grid">
          <div>
            {flowItems.map((f) => (
              <div className="flow-item" key={f.title}>
                <h3>{f.title}</h3>
                <p className="flow-bar">{f.bar}</p>
                <p className="flow-desc">{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="terminal" aria-hidden="true">
            <div className="term-bar">
              <span className="tdot td-r"></span>
              <span className="tdot td-y"></span>
              <span className="tdot td-g"></span>
              <span className="term-title">darkdefensive — grc command center</span>
            </div>
            <div className="term-body">
              {terminalRows.map((r) => (
                <div className="t-row" key={r.ts}>
                  <span className="t-ts">{r.ts}</span>
                  <span className={`t-lvl ${r.cls}`}>{r.lvl}</span>
                  <span className="t-msg">{r.msg}</span>
                </div>
              ))}
              <div className="t-row">
                <span className="t-ts">now</span>
                <span className="t-lvl tok">LIVE</span>
                <span className="t-msg">
                  Continuous compliance monitoring active
                  <span className="blink">_</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FRAMEWORKS */}
      <div className="band band-net">
        <div className="wrap section" id="frameworks">
          <h2 style={{ textAlign: "center" }}>
            Map once. <span className="accent">Report across</span> many frameworks.
          </h2>
          <div className="pill-rows">
            <ul className="pill-row" aria-label="Supported frameworks">
              {frameworksRow1.map((f) => (
                <li className="pill" key={f}>{f}</li>
              ))}
            </ul>
            <ul className="pill-row" aria-label="Additional frameworks">
              {frameworksRow2.map((f) => (
                <li className="pill" key={f}>{f}</li>
              ))}
            </ul>
          </div>

          <div className="split">
            <article className="feature-card">
              <h3>Real-Time Dark Web Analysis</h3>
              <p className="sub">Proactive Defense. Compliance Alignment. Reduced Risk.</p>
              <ul>
                <li>Controls &amp; Evidence</li>
                <li>Risk Heatmaps</li>
                <li>Audit Ready</li>
                <li>AI Assistant</li>
              </ul>
            </article>
            <h2>
              Dark Defensive stands at the forefront of cybersecurity innovation
            </h2>
          </div>
        </div>
      </div>

      {/* TESTIMONIALS */}
      <div className="wrap section">
        <h2 style={{ textAlign: "center" }}>
          Trusted to <span className="accent">Protect</span> What Matters
        </h2>
        <div className="marquee" aria-label="Customer testimonials">
          <div className="marquee-track">
            {[...testimonials, ...testimonials].map((t, i) => (
              <div className="quote-card" key={i}>
                <p>&ldquo;{t.quote}&rdquo;</p>
                <p className="who">{t.who}</p>
                <p className="role">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="wrap section">
        <div className="cta-panel">
          <p className="eyebrow">Ready to modernize GRC?</p>
          <h2>
            Make audit readiness <span className="accent">continuous</span>
            <br />~ not seasonal
          </h2>
          <p>
            See how Dark Defensive helps GRC teams do big work with AI-assisted
            evidence, control automation, risk prioritization, and executive-ready
            reporting.
          </p>
          <div className="dd-btn-row">
            <Link className="dd-btn dd-btn-primary" to="/contact-us">
              Request a Demo &gt;
            </Link>
            <Link className="dd-btn dd-btn-secondary" to="/contact-us">
              Talk to the Team
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
